/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement, useState, useEffect } from 'react';
import { FieldProps, ErrorMessage, useFormikContext, getIn } from 'formik';
import DatePicker from 'react-datepicker';
import MaskedTextInput from 'react-text-mask';
import 'react-datepicker/dist/react-datepicker.css';
import classnames from 'classnames';
import Calendar from '../../images/calendar.svg';
import styles from './DateInput.module.scss';
import './DatePickerOverride.css';

interface iProps extends FieldProps {
  title?: string;
  isMandatory?: boolean;
  maxDate?: Date;
  minDate?: Date;
  autocomplete?: string;
  isDisabled?: boolean;
}

const DateInput = ({ field, form, title = '', isMandatory = false, maxDate, minDate, autocomplete = 'on', isDisabled = false }: iProps): ReactElement => {
  const { name, value } = field;
  const { setFieldValue, setFieldError, setFieldTouched } = form;
  const { errors, touched }: { errors: any; touched: any } = useFormikContext();
  const [hasError, setHasError] = useState(false);
  const [isFirstTouch, setFirstTouch] = useState(true);

  const [isVisibleCalendar, setVisibleCalendar] = useState(false);
  useEffect(() => {
    setHasError(getIn(errors, field.name) && getIn(touched, field.name));
  }, [errors, touched]);

  const renderTitle = (): ReactElement => (
    <div
      className={classnames({
        [styles.label]: true,
        [styles.titleError]: hasError,
      })}>
      <span className={styles.titleBold}>{title}</span>
      {!isMandatory && <span className={styles.optional}> ( optional )</span>}
    </div>
  );

  return (
    <div className={styles.container}>
      {title && renderTitle()}
      <div className={styles.dateInputContainer} onBlur={() => form.setFieldTouched(name, true)}>
        <DatePicker
          {...field}
          wrapperClassName={styles.myDatepickerWrapper}
          value={value && typeof value === 'string' ? new Date(value) : value}
          selected={value && typeof value === 'string' ? new Date(value) : value}
          onChange={async (val, event) => {
            const input = event?.currentTarget as HTMLInputElement;
            const dateString = input.value;
            const parseDateStringRegex = new RegExp('^([0-9]+)/([0-9]+)/([0-9]+)$');
            if (dateString) {
              const dateStringComponents = dateString.match(parseDateStringRegex);
              if (dateStringComponents) {
                if (dateStringComponents[1].length === 2 && dateStringComponents[2].length === 2 && dateStringComponents[3].length === 4) {
                  await setFieldValue(name, val);
                }
              }
            } else if (!dateString && val) {
              await setFieldValue(name, val);
            }
            if (!dateString) {
              await setFieldValue(name, val);
              await setFieldTouched(name);
            }
          }}
          autoComplete={autocomplete}
          onChangeRaw={async (val) => {
            const input = val?.currentTarget as HTMLInputElement;
            const dateString = input.value;
            const parseDateStringRegex = new RegExp('^([0-9]+)/([0-9]+)/([0-9]+)$');
            if (dateString) {
              const dateStringComponents = dateString.match(parseDateStringRegex);
              if (dateStringComponents) {
                if (!(dateStringComponents[1].length === 2 && dateStringComponents[2].length === 2 && dateStringComponents[3].length === 4)) {
                  await setFieldValue(name, undefined);
                } else {
                  const dateParts = dateString.split('/');
                  const dateObject = new Date(+dateParts[2], +dateParts[1] - 1, +dateParts[0]);
                  await setFieldValue(name, dateObject);
                }
              }
            }
          }}
          customInput={<MaskedTextInput id="DOBInput" guide={false} type="text" mask={[/^[0-3\b]+$/, /\d/, '/', /^[0-1\b]+$/, /\d/, '/', /^[1-2\b]+$/, /\d/, /\d/, /\d/]} />}
          dateFormat="dd/MM/yyyy"
          onFocus={() => {
            setVisibleCalendar(false);
            if (isFirstTouch) setFirstTouch(false);
            else form.setFieldTouched(name, true);
          }}
          open={isVisibleCalendar}
          className={classnames({ [styles.date]: true, [styles.inputError]: hasError })}
          placeholderText="DD/MM/YYYY"
          maxDate={maxDate}
          minDate={minDate}
          showYearDropdown
          popperClassName={styles.popper}
          // popperModifiers={[
          //   {
          //     name: 'arrow',
          //     options: {
          //       padding: 12,
          //     },
          //   },
          // ]}
          onSelect={() => setVisibleCalendar(false)}
          onBlur={() => setVisibleCalendar(false)}
          onCalendarClose={async () => {
            await form.setFieldTouched(name, true, false);
          }}
          onClickOutside={() => setVisibleCalendar(false)}
          scrollableYearDropdown
          // dropdownMode="select"
          yearDropdownItemNumber={80}
          disabled={isDisabled}
        />
        <button
          type="button"
          className={styles.calendarPopupBtn}
          disabled={isDisabled}
          onClick={() => {
            setVisibleCalendar(true);
          }}>
          <img className={styles.iconContainer} src={Calendar} alt="icon" />
        </button>
      </div>
      <ErrorMessage className={styles.errorMsg} name={name} component="span" />
    </div>
  );
};

export default DateInput;
